import { liveInstancePath, scopeOf, withFallbackScope } from '../resources/liveNode.js';
import { mergeInstanceRoot } from '../resources/mergeInstanceRoot.js';
import { followTrailIn } from '../resources/sceneTrail.js';
import { mayHandBack, seatInFile } from '../resources/seatEscaped.js';
import { withRootFallbacksDecided } from '../resources/ownerHandOff.js';
import { cachedUniqueNamesBelowRoot } from '../utils/uniqueNames.js';
import { glbPathOf, loneGlbRootPath } from '../resources/glbSceneRoot.js';
import { glbSceneRootChildren } from './internal/glb-scene-root/glbHierarchy.js';
import { splitAttachedChildren } from './internal/glb-scene-root/glbNodeOverrides.js';
import { joinPath } from '../utils/nodePath.js';
import { nodePathNames } from '../godot/string/nodePath.js';
import { sameElements } from '../utils/sameElements.js';
import { perObject } from '../utils/perObject.js';
import { NOTHING_CACHED, answeredOnce, keptWhileAnswered } from './cacheSources.js';
/** The scene cache of `ctx` as the walks over it read it: each path asked of the loader once. */
const snapshotScenes = perObject((ctx) => answeredOnce(ctx.sceneCache));
/**
 * The node as the tree and viewport render it: a single-root `.tscn` instance
 * collapses into its sub-scene root (Instance root merge, ADR-0013), carrying the
 * sub-scene's scope. A `.glb` instance, a non-instance, a scene not yet cached, a scene
 * Godot refuses to instantiate and a scene already in `scope.instancedScenePaths` (cyclic
 * instancing) return unchanged.
 *
 * An inherited sub-scene root collapses over its base first, as Godot instantiates the base
 * before the inheriting file's headings land (packed_scene.cpp:227-231), and the host merges
 * onto that composed root. The chain stops at a scene that already encloses the node, so a
 * scene that inherits or instances itself ends.
 */
function collapseLiveNode(node, scope, sceneCache) {
    const own = scopeOf(node, scope);
    return collapseInstance(node, own, loadedInstance(node, own, sceneCache), sceneCache);
}
/** The collapse of `node` over the scene it instances, unchanged where there is none or Godot refuses it. */
function collapseInstance(node, own, instanced, sceneCache) {
    if (!instanced || instanced.isRefused)
        return node;
    return collapseOver(node, own, instanced.scenePath, instanced.cached, sceneCache);
}
/**
 * The `res://` path of the scene `node` instances where its scope is `own` ({@link scopeOf}), or null where it
 * instances none or a scene that already encloses it. Godot never loads a cyclic instance, so
 * nothing of it is below the node.
 */
function loadableScenePath(node, own) {
    const scenePath = liveInstancePath(node, own);
    const enclosingScenePaths = own.instancedScenePaths ?? [];
    return scenePath && !enclosingScenePaths.includes(scenePath) ? scenePath : null;
}
/**
 * The loaded scene `node` instances where its scope is `own`, or null where it instances none,
 * a scene that already encloses it, or one the cache has not loaded.
 */
function loadedInstance(node, own, sceneCache) {
    const scenePath = loadableScenePath(node, own);
    if (!scenePath)
        return null;
    const cached = sceneCache.getCached(scenePath);
    if (!cached)
        return null;
    return { scenePath, cached, isRefused: isRefusedScene(cached, scenePath, own, sceneCache) };
}
/**
 * Whether Godot refuses to instantiate `cached`: the parse says so, or its root inherits a base
 * that Godot refuses (`packed_scene.cpp:227-232`), or one of its nodes instances a scene that Godot
 * refuses (`:262-265`). `outer` is the scope that instances `cached`, whose enclosing scenes the walk
 * extends, so a scene that instances itself ends it.
 */
function isRefusedScene(cached, scenePath, outer, sceneCache) {
    if (cached.refusesToInstantiate)
        return true;
    const chain = [...(outer.instancedScenePaths ?? []), scenePath];
    return refusals(cached, chain, { scenes: sceneCache }, ({ scenes }) => holdsRefusedInstance(cached, chain, scenes));
}
/** Whether a node of `cached`, its root included, is an instance of a scene that Godot refuses. */
function holdsRefusedInstance(cached, chain, scenes) {
    const content = contentScopeOf(cached, chain);
    const isRefusedAt = (node) => (loadedInstance(node, scopeOf(node, content), scenes)?.isRefused ?? false) ||
        node.children.some(isRefusedAt);
    return cached.nodes.some(isRefusedAt);
}
/** The collapse of `node` over `cached`, the scene at `scenePath` that the caller read once. */
function collapseOver(node, outer, scenePath, cached, sceneCache) {
    const content = contentScopeOf(cached, [...(outer.instancedScenePaths ?? []), scenePath]);
    const subScene = composedSubScene(cached, content, sceneCache);
    // A composed root's nodes resolve in its base's scope, which its own `scope` carries.
    const rootContent = subScene.nodes.length === 1 ? scopeOf(subScene.nodes[0], content) : content;
    const cache = { collapse: collapserOver(sceneCache), followTrail: followTrailIn(cached, sceneCache) };
    return mergeInstanceRoot(node, subScene, { outer, content: rootContent }, cache) ?? node;
}
/**
 * `collapseLiveNode` over `sceneCache`, as the seating takes it. It keeps each collapse, as an
 * owner hand-off asks the instance that hands it off before the frame seats that instance.
 */
function collapserOver(sceneCache) {
    const collapsedIn = perObject((node) => perObject((scope) => collapseLiveNode(node, scope, sceneCache)));
    return (node, scope) => collapsedIn(node)(scope);
}
/**
 * The sources that ask about one scene in one chain at once: the loader's cache, and the
 * single-path source of a tree row. Two keep each from replacing the other's composition.
 */
const COMPOSITIONS_PER_CHAIN = 2;
/**
 * Written only by {@link isRefusedScene}, keyed by the cached scene object and the chain of scenes
 * that encloses it, since the cycle check reads the chain. An answer stays valid while the cache
 * answers what the walk read.
 */
const refusals = keptWhileAnswered({
    sameVariant: sameElements,
    perVariant: COMPOSITIONS_PER_CHAIN,
});
/**
 * Written only by {@link composedSubScene}, keyed by the cached scene object. A scene composes once
 * for each chain of scenes that encloses it, since the cyclic-instancing check reads that chain.
 */
const compositions = keptWhileAnswered({ sameVariant: sameElements, perVariant: COMPOSITIONS_PER_CHAIN });
/**
 * A loaded sub-scene as it instantiates by itself: its sole root collapsed over the base it
 * inherits, with the children its own instances handed back seated, or the scene as cached where
 * nothing changes. A composition is reused while the cache still answers what it read, so every
 * instance shares one `%Name` table. The source may be the loader's live cache, which changes in place.
 */
function composedSubScene(cached, content, sceneCache) {
    const root = cached.nodes[0];
    if (cached.nodes.length !== 1 || !root || !(root.instance || mayHandBack(root)))
        return cached;
    // The recording source also sees the reads of a base's own composition, so a change of any base ends this entry.
    return compositions(cached, content.instancedScenePaths ?? [], { scenes: sceneCache }, ({ scenes }) => composedOverBases(cached, root, content, scenes));
}
/** `cached` with its sole `root` collapsed over its bases and seated, or the same scene where nothing changes. */
function composedOverBases(cached, root, content, sceneCache) {
    const collapsed = collapseLiveNode(root, content, sceneCache);
    const [seated] = seatedFile([collapsed], content, sceneCache, cached.nodes);
    return seated === root ? cached : { nodes: [seated] };
}
/**
 * The nodes of one file with the children their instances handed back seated where the file's own
 * walk puts them. The same array where no instance hands one back.
 *
 * @param authored the file's nodes, collapsed over the base an inherited root names
 * @param scope the scope the file's nodes resolve against
 * @param seating the nodes as the parse seated them, whose `%Name` claims the file answers a walk with
 */
function seatedFile(authored, scope, sceneCache, seating) {
    const first = authored[0];
    if (authored.length !== 1 || !first || !(mayHandBack(first) || first.escaped))
        return authored;
    const collapse = collapserOver(sceneCache);
    // Only a root that inherits no base claims for a root fallback, and such a root does not collapse.
    const roots = first === seating[0] ? withRootFallbacksDecided(seating, scope, collapse) : authored;
    const root = roots[0];
    // A root that inherits a base, not merged over it yet, hands back through its collapse.
    const isUnmerged = root.instance !== undefined && root.uniqueNames === undefined;
    const handedBack = (isUnmerged ? collapse(root, scope) : root).escaped ?? [];
    const table = root.uniqueNames;
    const seated = seatInFile({
        root,
        scope: scopeOf(root, scope),
        claimed: (key) => (table ? table.paths() : cachedUniqueNamesBelowRoot(seating)).get(key),
        handedBack,
    }, collapse);
    return seated === root ? roots : [seated];
}
/**
 * The scope a loaded sub-scene's own nodes resolve against. An absent pool reads
 * as empty, never as the caller's: these ids belong to the sub-scene's file.
 */
function contentScopeOf(cached, instancedScenePaths) {
    return {
        externalResources: cached.externalResources ?? [],
        internalResources: cached.internalResources ?? [],
        instancedScenePaths,
    };
}
/**
 * The root {@link SceneScope} for a path or tree walk: the root scene's own pools. A copy, not
 * the context itself, so no scope a node keeps holds the loader's caches.
 */
export function rootScope(ctx) {
    return { externalResources: ctx.externalResources, internalResources: ctx.internalResources };
}
function inlineGroup(children, scope) {
    return { origin: 'inline', children, scope };
}
/**
 * What is below a node that does not collapse: one `inline` group for a non-instance, and one
 * `glb` group for a GLBSceneRoot. A single-root instance collapses, and {@link mergedGroups}
 * gives its groups.
 * The node that instances a lone GLB is the GLB root, so a loaded GLB's objects are `glb`
 * groups and its own children `inline` groups, in the order `index=` gives. `scope` is
 * the one of the group the node sits in, which the node's own `scope` replaces. A sub-scene's
 * groups read the sub-scene's pools from the cache, and their `instancedScenePaths` gain the
 * sub-scene. An instance of a scene that already encloses it (cyclic instancing), and of one Godot
 * refuses to instantiate, gets one `inline` group.
 */
function liveChildGroups(node, own, instanced, glbCache) {
    const inlineOnly = () => [inlineGroup(node.children, own)];
    // GLB nodes carry no instance refs, so they stay in the outer scope.
    const glbPath = glbPathOf(node);
    const glbObject = glbPath ? glbCache?.getCached(glbPath) : undefined;
    if (glbObject)
        return glbInstanceGroups(glbObject, node.children, own);
    if (!instanced || instanced.isRefused)
        return inlineOnly();
    // A lone GLB is the instancing node's own content: its objects sit directly below that node, as
    // the GLB root's children do in Godot (packed_scene.cpp:261-264). Until the GLB loads, the
    // node's children stay inline.
    const instancedGlbPath = loneGlbRootPath(instanced.cached.nodes);
    const object = instancedGlbPath ? glbCache?.getCached(instancedGlbPath) : undefined;
    return object ? glbInstanceGroups(object, node.children, own) : inlineOnly();
}
/**
 * The groups of a collapsed instance. A sub-scene root that instances a loaded lone GLB leaves
 * the merged node the GLB root, so its objects sit directly below it.
 */
function mergedGroups(merged, outer, sceneCache, glbCache) {
    const scope = scopeOf(merged, outer);
    const object = instancedGlbObject(merged, scope, sceneCache, glbCache);
    if (!object)
        return [{ origin: 'merged', children: merged.children, scope }];
    return glbInstanceGroups(object, merged.children, scope);
}
/** The loaded GLB `node` instances as the only root of its sub-scene, or undefined. */
function instancedGlbObject(node, scope, sceneCache, glbCache) {
    const scenePath = loadableScenePath(node, scope);
    if (!scenePath)
        return undefined;
    const glbPath = loneGlbRootPath(sceneCache.getCached(scenePath)?.nodes ?? []);
    return glbPath ? (glbCache?.getCached(glbPath) ?? undefined) : undefined;
}
/**
 * The groups of a node that instances a lone GLB: the GLB's objects, with the host's typed children
 * below the GLB nodes their parent paths name, and the host's other children after them, or at the
 * `index=` they state. Each typed child takes the host's scope when it carries none, since the GLB
 * scene's pools are not the ones it was authored against.
 */
function glbInstanceGroups(object, hostChildren, hostScope) {
    const { attached, rest } = splitAttachedChildren(object, hostChildren);
    const stamped = attached.map((child) => withFallbackScope(child, hostScope));
    const ordered = glbSceneRootChildren(object, stamped, rest);
    if (rest.length === 0)
        return [{ origin: 'glb', children: ordered, scope: hostScope }];
    const hostOwn = new Set(rest);
    const runs = [];
    for (const child of ordered) {
        const origin = hostOwn.has(child) ? 'inline' : 'glb';
        if (runs.at(-1)?.origin === origin)
            runs.at(-1).children.push(child);
        else
            runs.push({ origin, children: [child] });
    }
    return runs.map((run) => ({ ...run, scope: hostScope }));
}
/**
 * The step for `node` where it sits in a group of scope `groupScope`. An instance merges once
 * here, so no reader merges a second time. Readers take it through {@link LiveTree.stepOf}, which
 * keeps it.
 */
function liveStep(node, groupScope, sceneCache, glbCache) {
    const instancePath = liveInstancePath(node, groupScope);
    const own = scopeOf(node, groupScope);
    const instanced = loadedInstance(node, own, sceneCache);
    const merged = collapseInstance(node, own, instanced, sceneCache);
    if (merged === node) {
        const groups = liveChildGroups(node, own, instanced, glbCache);
        return {
            node,
            isMerged: false,
            instancePath,
            scope: own,
            isRefusedInstance: instanced?.isRefused ?? false,
            groups,
        };
    }
    const groups = mergedGroups(merged, own, sceneCache, glbCache);
    return {
        node: merged,
        isMerged: true,
        instancePath,
        scope: groups[0].scope,
        isRefusedInstance: false,
        groups,
    };
}
/** Whether `group` holds GLB objects, which draw after the node's authored children. */
export function isInjected(group) {
    return group.origin === 'glb';
}
/** Whether `group` is what the node instances, which the instance node owns as the root of it. */
export function holdsInstanceContent(group) {
    return group.origin === 'merged' || group.origin === 'glb';
}
/** The first child called `name` across `groups`, as a path walk descends, with the group it sits in. */
function liveChildNamed(groups, name) {
    for (const group of groups) {
        const raw = group.children.find((n) => n.name === name);
        if (raw)
            return { raw, group };
    }
    return undefined;
}
/**
 * The walk {@link liveNodeChain} and {@link resolveLiveEntry} share: descend a
 * path, collapsing each segment, or `null` if any is unresolvable. The groups
 * stay apart rather than one flat list, so the matched node descends in its own
 * group's scope.
 */
function liveChainLinks(path, roots, ctx) {
    const segments = nodePathNames(path);
    if (segments.length === 0)
        return null;
    const tree = liveTreeOf(roots, ctx);
    const links = [];
    let livePath = '';
    for (const segment of segments) {
        livePath = joinPath(livePath, segment);
        const link = tree.at(livePath);
        if (!link)
            return null;
        links.push({ raw: link.raw, collapsed: link.step.node });
    }
    return links;
}
/**
 * The live tree over one cache snapshot, each node's step taken once however many paths pass
 * through it. Every question over one context gets the tree {@link liveTreeOf} returns.
 *
 * @canonical A walk of the Live scene tree
 *   Fold it with `LiveTree.fold`, read one path with `LiveTree.at`, and one step with `LiveTree.stepOf`.
 */
export class LiveTree {
    roots;
    ctx;
    /** The groups a walk starts from: the outer roots, in the root scene's scope. */
    rootGroups;
    /**
     * `roots` with the headings their instances hand back seated where the file's walk puts them. The
     * rows and the viewport draw from these, since each instance they enter collapses on its own.
     */
    seatedRoots;
    /** Written only by `at`. A null marks a path the loaded tree does not contain. */
    links = new Map();
    scenes;
    sources;
    constructor(roots, ctx) {
        this.roots = roots;
        this.ctx = ctx;
        this.scenes = snapshotScenes(ctx);
        this.sources = { scenes: this.scenes, glbs: ctx.glbCache ?? NOTHING_CACHED };
        const scope = rootScope(ctx);
        this.seatedRoots = seatedFile(roots, scope, this.scenes, roots);
        this.rootGroups = [inlineGroup(this.seatedRoots, scope)];
    }
    /** The root nodes of the loaded scene at `scenePath`, or undefined while it is not loaded. */
    sceneNodes(scenePath) {
        return this.scenes.getCached(scenePath)?.nodes;
    }
    /**
     * Follows `trail` in the loaded scene at `scenePath`, where an instance holds the owner an
     * `owner_uid_path=` names. A scene that is not loaded yet makes the trail wait.
     */
    followTrail(scenePath, trail) {
        const scene = this.scenes.getCached(scenePath);
        return scene
            ? followTrailIn(scene, this.scenes)(trail)
            : { status: scene === null ? 'missing' : 'pending' };
    }
    /** The node at the live path `path`, or null where the loaded tree stops. */
    at(path) {
        const cached = this.links.get(path);
        if (cached !== undefined)
            return cached;
        const slash = path.lastIndexOf('/');
        const parentGroups = slash < 0 ? this.rootGroups : this.at(path.slice(0, slash))?.step.groups;
        const found = parentGroups && liveChildNamed(parentGroups, path.slice(slash + 1));
        const link = found ? this.linkTo(found.raw, found.group, path) : null;
        this.links.set(path, link);
        return link;
    }
    /** The link for `raw` where it sits in `group` at `path`. */
    linkTo(raw, group, path) {
        return { raw, step: this.stepOf(raw, group.scope), path, group };
    }
    /**
     * The step for `node` where it sits in `scope`, taken once. A node is found by itself and its
     * scope, never by its path: two siblings may share a name, and a sub-scene's nodes sit at
     * every instance of it. A tree over a later snapshot gives the same step while every answer the
     * step read is unchanged.
     */
    stepOf(node, scope) {
        return stepsByNode(node, scopeOf(node, scope), this.sources, ({ scenes, glbs }) => liveStep(node, scope, scenes, glbs));
    }
    /**
     * Depth-first fold of the tree. `visit` gets each node with what its parent passed down, and a
     * `descend` that folds the node's children, in group order, with what this node passes down. A
     * visit that never calls `descend` prunes the children. The results come back in order.
     * It starts at the roots unless given the link of a node, so a fold of its subtree reads the
     * steps the other walks of this tree took.
     */
    fold(inherited, visit, from) {
        const results = [];
        for (const group of from ? from.step.groups : this.rootGroups) {
            for (const raw of group.children) {
                const link = this.linkTo(raw, group, joinPath(from?.path ?? '', raw.name));
                const descend = (passedDown) => this.fold(passedDown, visit, link);
                results.push(visit(link, inherited, descend));
            }
        }
        return results;
    }
}
/**
 * Written only by {@link LiveTree.stepOf}: the steps of a node under each scope it was asked in.
 * They outlive the tree that took them, since a load that a step did not read leaves it as it is.
 * A sub-scene's node is asked under each chain of scenes that encloses it, so a node keeps every
 * scope it was asked in: an evicting limit would take the step again at each load. The scopes are
 * the scenes' own and finite, and the entries clear when nothing holds the node any more.
 */
const stepsByNode = keptWhileAnswered({ sameVariant: sameScope, perVariant: 1 });
const NO_PATHS = [];
function sameScope(a, b) {
    return (a === b ||
        (a.externalResources === b.externalResources &&
            a.internalResources === b.internalResources &&
            sameElements(a.instancedScenePaths ?? NO_PATHS, b.instancedScenePaths ?? NO_PATHS)));
}
/**
 * Written only by {@link liveTreeOf}: one tree for each context and roots. A tree clears when
 * nothing holds its context or its roots any more.
 */
const liveTreesOver = perObject((ctx) => perObject((roots) => new LiveTree(roots, ctx)));
/** The live tree every question over `ctx` shares, so a node collapses once for all of them. */
export function liveTreeOf(roots, ctx) {
    return liveTreesOver(ctx)(roots);
}
/**
 * The collapsed nodes from the root down to the path's target, or `null` if any
 * segment is unresolvable. Each element is collapsed, so an ancestor-transform
 * walk such as `node2dWorldPosition` sees an instance's merged properties.
 */
export function liveNodeChain(path, roots, ctx) {
    const links = liveChainLinks(path, roots, ctx);
    return links ? links.map((l) => l.collapsed) : null;
}
/**
 * The collapsed node at a path with its originating instance ref, or `null` if
 * any segment is unresolvable. A single-root sub-scene collapses into its
 * instance node (ADR-0013), so the root's own name is not a path segment.
 */
export function resolveLiveEntry(path, roots, ctx) {
    const links = liveChainLinks(path, roots, ctx);
    if (!links || links.length === 0)
        return null;
    const last = links[links.length - 1];
    return { node: last.collapsed, instanceRef: last.raw.instance };
}
/**
 * Depth-first walk of the live tree, calling `visit` with each collapsed node and its full path.
 *
 * @canonical A walk of the Live scene tree
 */
export function walkLiveTree(roots, ctx, visit, descend) {
    liveTreeOf(roots, ctx).fold(undefined, ({ step, path }, _, descendInto) => {
        visit({ node: step.node, path });
        // `descend` prunes only the children, so a consumer can count a boundary
        // without counting what is behind it.
        if (!descend || descend(step.node))
            descendInto();
    });
}
/**
 * Every live-tree node whose collapsed identity satisfies `predicate`, with its
 * path. `descend` returns false for a node whose children are not collected. A
 * sub-viewport's content shows only through its surface, so the 2D-content hint
 * stops there while the scene tree panel does not.
 */
export function collectLiveNodes(roots, ctx, predicate, descend) {
    const out = [];
    walkLiveTree(roots, ctx, (entry) => {
        if (predicate(entry.node))
            out.push(entry);
    }, descend);
    return out;
}
//# sourceMappingURL=liveSceneTree.js.map